import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ImageOff, ArrowRight, Loader2, X, ChevronLeft, ChevronRight } from "lucide-react";
import MarketingShell from "../components/MarketingShell";
import { useAuth } from "../lib/auth";
import { listPortfolio } from "../lib/api";

// iter-144 — Portfolio page is now backed by the /api/portfolio
// endpoint. Superadmins upload/manage from Admin → Website tab.

const FILTERS = [
    { key: "all", label: "All" },
    { key: "general", label: "General" },
    { key: "excavation", label: "Excavation" },
    { key: "septic", label: "Septic Services" },
    { key: "demolition", label: "Demolition" },
    { key: "site", label: "Site Services" },
];

const CAT_LABEL = {
    general: "General",
    excavation: "Excavation",
    demolition: "Demolition",
    septic: "Septic",
    site: "Site Services",
};

export default function PortfolioPage() {
    const navigate = useNavigate();
    const { user } = useAuth();
    useEffect(() => { /* iter-166 — auto-redirect removed; user can browse freely */ }, [user, navigate]);

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");

    useEffect(() => {
        let dead = false;
        (async () => {
            try {
                const rows = await listPortfolio();
                if (!dead) setItems(rows);
            } catch {
                if (!dead) setItems([]);
            } finally {
                if (!dead) setLoading(false);
            }
        })();
        return () => { dead = true; };
    }, []);

    const visible = useMemo(
        () => (filter === "all" ? items : items.filter((it) => it.category === filter)),
        [items, filter],
    );

    // iter-166 — Lightbox: click any tile to open a full-size preview.
    // Left/right arrows (both on-screen and keyboard) walk the filtered
    // set so the user can flip through matches without closing.
    const [lightboxIdx, setLightboxIdx] = useState(null);
    const closeLightbox = () => setLightboxIdx(null);
    const showPrev = () => setLightboxIdx((i) => (i === null ? null : (i - 1 + visible.length) % visible.length));
    const showNext = () => setLightboxIdx((i) => (i === null ? null : (i + 1) % visible.length));
    useEffect(() => {
        if (lightboxIdx === null) return undefined;
        const onKey = (e) => {
            if (e.key === "Escape") closeLightbox();
            else if (e.key === "ArrowLeft") showPrev();
            else if (e.key === "ArrowRight") showNext();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lightboxIdx, visible.length]);
    // If the filter changes while a lightbox is open, close it — the
    // index might no longer point to a valid tile.
    useEffect(() => { setLightboxIdx(null); }, [filter]);
    const lightboxItem = lightboxIdx !== null ? visible[lightboxIdx] : null;

    return (
        <MarketingShell testid="portfolio-page">
            <section
                className="hero-dark relative h-[42vh] min-h-[320px] flex items-end text-white"
                data-testid="portfolio-hero"
            >
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: "url(/images/services/septic.jpg)" }}
                    aria-hidden="true"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-slate-900/30 via-slate-900/50 to-slate-900/80" aria-hidden="true" />
                <div className="relative z-10 max-w-6xl mx-auto w-full px-4 sm:px-8 pb-10">
                    <p className="text-xs uppercase tracking-[0.3em] font-bold text-[#00A3FF]">Premiere excavation services in Barrie</p>
                    <h1 className="mt-2 text-4xl sm:text-5xl font-bold tracking-tight">
                        Steeltooth Contracting Portfolio
                    </h1>
                </div>
            </section>

            <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
                <div
                    className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 mb-8"
                    data-testid="portfolio-filters"
                    role="tablist"
                >
                    {FILTERS.map((f) => {
                        const active = filter === f.key;
                        return (
                            <button
                                key={f.key}
                                type="button"
                                role="tab"
                                aria-selected={active}
                                onClick={() => setFilter(f.key)}
                                data-testid={`portfolio-filter-${f.key}`}
                                className={`px-4 py-2 rounded-full text-xs sm:text-sm font-bold uppercase tracking-wider border transition-colors ${
                                    active
                                        ? "bg-[#007AFF] border-[#007AFF] text-white"
                                        : "bg-white border-slate-300 text-slate-700 hover:border-[#007AFF] hover:text-[#007AFF]"
                                }`}
                            >
                                {f.label}
                            </button>
                        );
                    })}
                </div>

                {loading ? (
                    <div className="text-center py-16 text-slate-500" data-testid="portfolio-loading">
                        <Loader2 className="w-6 h-6 mx-auto animate-spin" />
                    </div>
                ) : visible.length === 0 ? (
                    <div className="text-center py-16 text-slate-500" data-testid="portfolio-empty">
                        <ImageOff className="w-8 h-8 mx-auto mb-2 text-slate-400" />
                        <div>No projects yet — check back soon.</div>
                    </div>
                ) : (
                    <div
                        className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3"
                        data-testid="portfolio-grid"
                    >
                        {visible.map((item, idx) => (
                            <button
                                type="button"
                                key={item.id}
                                onClick={() => setLightboxIdx(idx)}
                                className="relative aspect-[4/3] rounded-md overflow-hidden bg-slate-800 group focus:outline-none focus:ring-2 focus:ring-[#00A3FF]"
                                data-testid={`portfolio-tile-${item.id}`}
                                aria-label={`Open ${item.caption || CAT_LABEL[item.category] || "project"} full size`}
                            >
                                <img
                                    src={item.url}
                                    alt={item.caption || CAT_LABEL[item.category] || "Steeltooth project"}
                                    loading="lazy"
                                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                                />
                            </button>
                        ))}
                    </div>
                )}

                <div className="mt-14 text-center">
                    <p className="text-xs uppercase tracking-[0.25em] text-slate-500 font-bold mb-2">Have a project like these?</p>
                    <Link
                        to="/contact"
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-[#007AFF] hover:bg-[#0369A1] text-white font-bold"
                        data-testid="portfolio-cta-contact"
                    >
                        Get in touch <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
            </div>

            {/* iter-166 — Lightbox overlay. hero-dark on the wrapper so
                the close/nav chrome stays readable in light theme. */}
            {lightboxItem && (
                <div
                    className="hero-dark fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
                    onClick={closeLightbox}
                    data-testid="portfolio-lightbox"
                    role="dialog"
                    aria-modal="true"
                >
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); closeLightbox(); }}
                        className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
                        data-testid="portfolio-lightbox-close"
                        aria-label="Close"
                    >
                        <X className="w-5 h-5" />
                    </button>
                    {visible.length > 1 && (
                        <>
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); showPrev(); }}
                                className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
                                data-testid="portfolio-lightbox-prev"
                                aria-label="Previous image"
                            >
                                <ChevronLeft className="w-6 h-6" />
                            </button>
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); showNext(); }}
                                className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
                                data-testid="portfolio-lightbox-next"
                                aria-label="Next image"
                            >
                                <ChevronRight className="w-6 h-6" />
                            </button>
                        </>
                    )}
                    <img
                        src={lightboxItem.url}
                        alt={lightboxItem.caption || CAT_LABEL[lightboxItem.category] || "Steeltooth project"}
                        className="max-w-[95vw] max-h-[92vh] object-contain rounded"
                        onClick={(e) => e.stopPropagation()}
                        data-testid="portfolio-lightbox-image"
                    />
                    {(lightboxItem.caption || lightboxItem.category) && (
                        <div
                            className="absolute bottom-6 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full bg-black/60 text-white text-sm max-w-[90vw] truncate"
                            onClick={(e) => e.stopPropagation()}
                            data-testid="portfolio-lightbox-caption"
                        >
                            {lightboxItem.caption || CAT_LABEL[lightboxItem.category]}
                            {visible.length > 1 && (
                                <span className="text-slate-400 ml-2">
                                    · {lightboxIdx + 1} / {visible.length}
                                </span>
                            )}
                        </div>
                    )}
                </div>
            )}
        </MarketingShell>
    );
}
